import './SendForm.sass';

import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { SendFormProps } from '$core/Chat/SendForm/SendForm.interface';
import { ActionBottomSheet, Input } from '$uikit';
import {
  IcDollar28,
  IcClose16,
  IcCloud28,
  IcGallery28,
  IcPaperclip28,
  IcSend28,
  IcVideo28,
  IcGift28,
} from '$assets';
import { Assistant } from '$shared/components';
import { aiCapsSelector } from '$store/aiCaps';
import { AttachPickerManagerRef } from '$shared/components/AttachPickerManager/AttachPickerManager.interface';
import AttachPickerManager from '$shared/components/AttachPickerManager/AttachPickerManager';
import { imActions, imAsPeerIdSelector } from '$store/im';
import {
  useChatInfo,
  useCurrentUserInfo,
  useDesktop,
  useEvent,
  useImActions,
  useTranslator,
  useUserInfo,
} from '$hooks';
import { RootState } from '$store/rootReducer';
import {
  blacklistHasBanFromMeSelector,
  blacklistHasBanToMeSelector,
} from '$store/blacklist';
import {
  ATTACHMENTS_COUNT_LIMIT,
  classNames,
  classNames as cn,
  genderText,
  scrollToBottom,
  scrollToBottomWithCheck,
} from '$utils';
import { stopWatch } from '$services';
import { goTo, showModal } from '$navigation/helper';
import { attachActions, attachItemsSelector, attachPhotosSelector } from '$store/attach';
import { CLOUD, SUBSCRIPTION_AUTHOR } from '$shared/constants/pages';
import { cloudActions } from '$store/cloud';
import { mainSelector } from '$store/main';
import { useRoute } from 'react-router5';
import { AttachPhoto, CurrencyEnum } from '$store/models';
import { getReplyPreview } from '$core/Chat/replyPreview';
import { balanceActions } from '$store/balance';
import { toastActions } from '$store/toast';
import { MainButton } from '@twa-dev/sdk/dist/react';
import router from '$navigation/router';

export enum AttachActions {
  UPLOAD_PHOTO = 'uploadPhoto',
  UPLOAD_VIDEO = 'uploadVideo',
  ATTACH_FROM_CLOUD = 'attachFromCloud',
}

export const SendForm: FC<SendFormProps> = ({ peerId, onFocus, onBlur }) => {
  const dispatch = useDispatch();
  const im = useImActions();
  const t = useTranslator();
  const asPeerId = useSelector(imAsPeerIdSelector);
  const isDesktop = useDesktop();
  const attachManager = useRef<AttachPickerManagerRef | null>(null);
  const info = useChatInfo(peerId);
  const pickerId = `im_${peerId}`;
  const attachments = useSelector((state: RootState) =>
    attachItemsSelector(state, pickerId),
  );
  const mediaAttachments: AttachPhoto[] = useSelector((state: RootState) =>
    attachPhotosSelector(state, pickerId),
  );
  const { mVerified } = useSelector(mainSelector);
  const sendBtn = useRef<HTMLDivElement | null>(null);
  const [photoInputKey, setPhotoInputKey] = useState(0);
  const [videoInputKey, setVideoInputKey] = useState(1);
  const peer = useUserInfo(peerId);
  const curUser = useCurrentUserInfo();
  const inputRef = useRef<any>(null);
  const [focused, setFocused] = useState(false);
  const [keyboardWatcher, setKeyboardWatcher] = useState(0);
  const [isActionShow, setActionsShown] = useState(false);
  const sendFormRef = useRef<HTMLDivElement>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const { route } = useRoute();
  const attachmentsLimit = ATTACHMENTS_COUNT_LIMIT;
  const mediaCount = mediaAttachments.length;

  const { decryptKeys } = useSelector(mainSelector);
  const canWrite = Object.keys(decryptKeys).length > 1;

  const aiCaps = useSelector(aiCapsSelector);
  const canShowAssistant =
    aiCaps.suggest && curUser?.verified === 2 && peer?.verified !== 2;

  useEffect(() => {
    return () => {
      stopWatch(keyboardWatcher);
    };
  }, []);

  useEffect(() => {
    const el = sendFormRef.current;
    if (!el) {
      return;
    }

    const updateHeight = () => {
      document.documentElement.style.setProperty(
        '--send-form-height',
        `${el.offsetHeight + 10}px`,
      );
      scrollToBottomWithCheck('chat_history');
    };

    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(el);

    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty('--send-form-height');
    };
  }, []);

  const isBannedFromMe = useSelector((root: RootState) =>
    blacklistHasBanFromMeSelector(root, peerId),
  );
  const isBannedToMe = useSelector((root: RootState) =>
    blacklistHasBanToMeSelector(root, peerId),
  );

  const handlePhotoChange = ({
    target: { files },
  }: React.ChangeEvent<HTMLInputElement>) => {
    if (!files) return;
    attachManager.current?.uploadImages(files);
    setPhotoInputKey(Date.now());
  };

  const handleVideoChange = ({
    target: { files },
  }: React.ChangeEvent<HTMLInputElement>) => {
    if (!files) {
      return;
    }

    attachManager.current?.uploadVideo(files[0]);
    setVideoInputKey(Date.now());
  };

  const handleTextChange = useCallback(
    ({ target }) => {
      im.sendTyping(peerId);
      dispatch(imActions.chatSetFormText({ peerId, text: target.value }));
    },
    [dispatch, im, peerId],
  );

  const handleSend = (e: any = null) => {
    try {
      e && e.preventDefault();
    } catch (err) {}
    if (attachments.length || info.text?.trim()) {
      im.sendMessage(peerId);
      inputRef.current?.resetHeight();
    }
  };

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend(e);
        setTimeout(() => scrollToBottom('chat_history'), 10);
      }
    },
    [handleSend],
  );

  const handleFocus = useCallback(() => {
    setFocused(true);
    onFocus();
  }, [setKeyboardWatcher, onFocus]);

  const handleBlur = useCallback(() => {
    setFocused(false);
    stopWatch(keyboardWatcher);
    onBlur();
  }, [setKeyboardWatcher, onBlur]);

  const handleResetReply = useCallback(() => {
    dispatch(imActions.chatSetReplyTo({ peerId, replyTo: null }));
  }, [dispatch, peerId]);

  const replyAuthor = useUserInfo(info.replyTo?.fromId ?? 0);
  const replyToLabel = useMemo(() => {
    if (!info.replyTo) {
      return null;
    }

    const author = replyAuthor ?? (info.replyTo.fromId === peerId ? peer : curUser);
    return [author?.firstName, author?.lastName].filter(Boolean).join(' ');
  }, [info.replyTo, peerId, peer, curUser, replyAuthor]);

  const replyPreview = useMemo(() => {
    return info.replyTo ? getReplyPreview(info.replyTo, t) : null;
  }, [info.replyTo, t]);

  const handleInvoice = useCallback(() => {
    if (peer.verified === 2) {
      showModal(`gift${peerId}`);
    } else {
      showModal('create_invoice', false, {
        peer_id: peerId,
      });
    }
  }, [peer, peerId]);

  const handleAction = (value: AttachActions) => {
    setActionsShown(false);
    if (mediaCount >= attachmentsLimit) {
      dispatch(
        toastActions.setToastFail(t('send_form_limit_error', { attachmentsLimit })),
      );
      return;
    }

    switch (value) {
      case AttachActions.UPLOAD_PHOTO:
        photoInputRef.current?.click();
        break;

      case AttachActions.UPLOAD_VIDEO:
        videoInputRef.current?.click();
        break;

      case AttachActions.ATTACH_FROM_CLOUD:
        dispatch(
          cloudActions.setSavedContentActions([
            imActions.chatSetFormText({ peerId, text: info.text }),
            attachActions.pushPhotos({ pickerId, photos: mediaAttachments }),
          ]),
        );
        dispatch(
          cloudActions.setAttachParams({
            pickerId,
            routeName: route.name,
            routeParams: route.params,
          }),
        );
        goTo(
          CLOUD,
          {},
          {
            limit: attachmentsLimit - mediaCount,
          },
        );
    }
  };

  const actions = useMemo(() => {
    const items = [
      {
        label: t('upload_photo'),
        value: AttachActions.UPLOAD_PHOTO,
        icon: <IcGallery28 />,
      },
      {
        label: t('upload_video'),
        value: AttachActions.UPLOAD_VIDEO,
        icon: <IcVideo28 />,
      },
    ];

    if (mVerified && !asPeerId) {
      items.push({
        label: t('attach_from_cloud'),
        value: AttachActions.ATTACH_FROM_CLOUD,
        icon: <IcCloud28 />,
      });
    }

    return items;
  }, [mVerified, asPeerId]);

  useEvent('touchstart', handleSend, sendBtn);
  useEvent('mousedown', handleSend, sendBtn);

  function renderContent() {
    if (isBannedToMe) {
      return (
        <div className="SendForm__banned">
          {peer.isTelegramUser
            ? genderText(peer?.sex, [
                t('send_form_bot_delete_you_male', { name: peer?.firstName ?? '' }),
                t('send_form_bot_delete_you_female', { name: peer?.firstName ?? '' }),
              ])
            : genderText(peer?.sex, [
                t('send_form_block_you_male', { name: peer?.firstName ?? '' }),
                t('send_form_block_you_female', { name: peer?.firstName ?? '' }),
              ])}
        </div>
      );
    }

    if (isBannedFromMe) {
      return <div className="SendForm__banned">{t('send_from_blocked')}</div>;
    }

    return (
      <>
        <AttachPickerManager
          mini
          ref={attachManager}
          pickerId={pickerId}
          asPeerId={asPeerId}
        />
        <div className="SendForm__cont">
          <div
            className="SendForm__button"
            onClick={() => {
              if (canWrite) {
                setActionsShown(true);
              } else {
                router.navigate(SUBSCRIPTION_AUTHOR, {
                  authorId: peerId,
                });
              }
            }}
          >
            <IcPaperclip28 />
          </div>
          {!asPeerId && (
            <div className="SendForm__button" onClick={handleInvoice}>
              {mVerified ? <IcDollar28 /> : <IcGift28 />}
            </div>
          )}
          <div
            className={classNames({
              SendForm__input_wrap: true,
              withReply: !!info.replyTo,
            })}
          >
            {info.replyTo && replyPreview && (
              <div className="SendForm__reply_to">
                {replyPreview.imageUrl && (
                  <div
                    className="SendForm__reply_to__img"
                    style={{ backgroundImage: `url(${replyPreview.imageUrl})` }}
                  />
                )}
                <div className="SendForm__reply_to__cont">
                  <div className="SendForm__reply_to__name">
                    {t('message_reply_to', { defaultValue: 'Reply to' })} {replyToLabel}
                  </div>
                  <div className="SendForm__reply_to__text">{replyPreview.text}</div>
                </div>
                <div className="SendForm__reply_to__cancel" onClick={handleResetReply}>
                  <IcClose16 />
                </div>
              </div>
            )}
            <Input
              autoFocus={isDesktop}
              multiline
              minHeight={40}
              padding={8}
              maxLines={4}
              placeholder={t('send_from_placeholder')}
              onChange={handleTextChange}
              value={info.text}
              ref={inputRef}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
            />
          </div>
          <div className="SendForm__button" ref={sendBtn}>
            <IcSend28 />
          </div>
        </div>
        <input
          className="SendForm__fileInput"
          ref={photoInputRef}
          key={photoInputKey}
          type="file"
          accept="image/*"
          multiple
          onChange={handlePhotoChange}
        />
        <input
          className="SendForm__fileInput"
          ref={videoInputRef}
          key={videoInputKey}
          type="file"
          accept="video/*"
          onChange={handleVideoChange}
        />
        {window.isWebApp && <MainButton text={t('send_button')} onClick={handleSend} />}
      </>
    );
  }

  return (
    <>
      <div ref={sendFormRef} className={cn('SendForm', { focused })}>
        <div className="SendForm-bg" />
        <div className="SendForm__cont_wrap">
          {canShowAssistant && <Assistant peerId={peerId} />}
          {renderContent()}
        </div>
      </div>
      <ActionBottomSheet
        title={t('message_actions_attach_title')}
        isOpened={isActionShow}
        options={actions}
        onChange={(action) => handleAction(action as AttachActions)}
        onClose={() => setActionsShown(false)}
        isAllGrouped={true}
      />
    </>
  );
};
